<template>
  <b-card>
    <template #header>
      <div class="d-flex align-items-center">
        <h5 class="mb-0">Диагностика обработки</h5>
        <b-icon
          v-b-tooltip.hover.right="'Статистика обработки данных: сколько считываний было назначено, пропущено, дублировано и переопределено вручную.'"
          icon="info-circle"
          class="ml-2 text-primary"
          font-scale="1"
        />
      </div>
    </template>

    <div v-if="!diagnostics" class="text-center text-muted py-4">
      <p>Нет данных диагностики.</p>
      <small>Запустите анализ для получения статистики обработки.</small>
    </div>

    <div v-else>
      <!-- Operational statistics -->
      <div v-if="statistics" class="mb-4">
        <h6>Ход гонки</h6>
        <div class="operational-stats mb-3">
          <div
            v-b-tooltip.hover="'Заявленные участники по конфигурации старта/дистанции.'"
            class="op-stat"
          >
            <span class="op-stat-value">{{ statistics.registeredCount != null ? statistics.registeredCount : statistics.totalParticipants }}</span>
            <span class="op-stat-label">Заявлено</span>
          </div>
          <div
            v-b-tooltip.hover="'Участники с отметкой на стартовом чекпоинте.'"
            class="op-stat"
          >
            <span class="op-stat-value text-success">{{ statistics.startedCount }}</span>
            <span class="op-stat-label">Стартовало</span>
          </div>
          <div
            v-b-tooltip.hover="'Участники с любой отметкой по трассе и без финиша (НС/ДСК исключены).'"
            class="op-stat"
          >
            <span class="op-stat-value text-primary">{{ statistics.onCourseCount != null ? statistics.onCourseCount : statistics.onTrackCount }}</span>
            <span class="op-stat-label">На дистанции</span>
          </div>
          <div
            v-b-tooltip.hover="'Участники, прошедшие финиш.'"
            class="op-stat"
          >
            <span class="op-stat-value text-success">{{ statistics.finishedCount }}</span>
            <span class="op-stat-label">Финишировало</span>
          </div>
          <div class="op-stat">
            <span class="op-stat-value text-warning">{{ statistics.dnfCount }}</span>
            <span class="op-stat-label">НФ</span>
          </div>
          <div class="op-stat">
            <span class="op-stat-value text-secondary">{{ statistics.dnsCount }}</span>
            <span class="op-stat-label">НС</span>
          </div>
          <div class="op-stat">
            <span class="op-stat-value text-danger">{{ statistics.dsqCount }}</span>
            <span class="op-stat-label">ДСК</span>
          </div>
        </div>
        <div v-if="statistics.perCheckpointCounts && Object.keys(statistics.perCheckpointCounts).length > 0">
          <h6>
            Уникальные участники по точкам
            <b-icon
              v-b-tooltip.hover.right="'Сколько уникальных участников имеют отметку на каждой точке трассы (повторы и круги объединены).'"
              icon="info-circle"
              class="ml-1 text-primary"
              font-scale="0.9"
            />
          </h6>
          <div class="checkpoint-stats">
            <div v-for="(count, cpName) in statistics.perCheckpointCounts" :key="cpName" class="cp-stat">
              <span class="cp-stat-name">{{ cpName }}</span>
              <span class="cp-stat-count">{{ count }}</span>
            </div>
          </div>
        </div>
        <div v-if="statistics.finishDistributionByGroup && Object.keys(statistics.finishDistributionByGroup).length > 0" class="mt-3">
          <h6>
            Финишировало по группам
            <b-icon
              v-b-tooltip.hover.right="'Распределение финишировавших по группам/категориям.'"
              icon="info-circle"
              class="ml-1 text-primary"
              font-scale="0.9"
            />
          </h6>
          <div class="checkpoint-stats">
            <div v-for="(count, groupName) in statistics.finishDistributionByGroup" :key="groupName" class="cp-stat">
              <span class="cp-stat-name">{{ groupName }}</span>
              <span class="cp-stat-count">{{ count }}</span>
            </div>
          </div>
        </div>
      </div>

      <hr v-if="statistics" />

      <!-- Overall stats -->
      <h6>Статистика обработки</h6>
      <b-row>
        <b-col md="3">
          <div 
            v-b-tooltip.hover="'Считывания успешно сопоставленные с участниками'"
            class="stat-card"
          >
            <div class="stat-value">{{ diagnostics.overall.assigned }}</div>
            <div class="stat-label">Назначено</div>
          </div>
        </b-col>
        <b-col md="3">
          <div 
            v-b-tooltip.hover="'Считывания не привязанные ни к одному участнику. Возможно неизвестный чип или ошибка.'"
            class="stat-card"
          >
            <div class="stat-value text-warning">{{ diagnostics.overall.unmatched }}</div>
            <div class="stat-label">Не сопоставлено</div>
          </div>
        </b-col>
        <b-col md="3">
          <div 
            v-b-tooltip.hover="'Повторные считывания с одного чипа в пределах интервала повторов'"
            class="stat-card"
          >
            <div class="stat-value">{{ diagnostics.overall.duplicates }}</div>
            <div class="stat-label">Дубликаты</div>
          </div>
        </b-col>
        <b-col md="3">
          <div 
            v-b-tooltip.hover="'Количество ручных корректировок времени, виртуальных считываний и статусов'"
            class="stat-card"
          >
            <div class="stat-value text-info">{{ diagnostics.overall.overridesUsed }}</div>
            <div class="stat-label">Переопределений</div>
          </div>
        </b-col>
      </b-row>

      <h6 class="mt-4">Детальная статистика</h6>
      <b-table
        :items="perUserStats"
        :fields="statsFields"
        small
        striped
        responsive
      >
        <template #cell(userId)="data">
          <strong>{{ data.value }}</strong>
        </template>
      </b-table>
    </div>
  </b-card>
</template>

<script>
import { BCard, BRow, BCol, BTable, BIcon, VBTooltip } from 'bootstrap-vue'

export default {
  name: 'DiagnosticsPanel',
  components: {
    BCard,
    BRow,
    BCol,
    BTable,
    BIcon,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  props: {
    diagnostics: {
      type: Object,
      default: null,
    },
    statistics: {
      type: Object,
      default: null,
    },
  },
  computed: {
    perUserStats() {
      if (!this.diagnostics || !this.diagnostics.perUser) {
        return []
      }
      
      return Object.entries(this.diagnostics.perUser).map(([userId, stats]) => ({
        userId: userId,
        ...stats,
      }))
    },
    
    statsFields() {
      return [
        { key: 'userId', label: 'Участник' },
        { key: 'assigned', label: 'Назначено' },
        { key: 'bufferedEarly', label: 'Рано' },
        { key: 'duplicates', label: 'Дубли' },
        { key: 'replacedEarlier', label: 'Заменено' },
        { key: 'overridesUsed', label: 'Переопределений' },
      ]
    },
  },
}
</script>

<style scoped>
.stat-card {
  text-align: center;
  padding: 1.143rem;
  background: #fff;
  border-radius: 0.428rem;
  margin-bottom: 1rem;
  border: 1px solid #ebe9f1;
  box-shadow: 0 2px 8px 0 rgba(34, 41, 47, 0.05);
  transition: all 0.2s;
}

.stat-card:hover {
  box-shadow: 0 4px 12px 0 rgba(34, 41, 47, 0.1);
  transform: translateY(-2px);
}

.stat-value {
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: 0.286rem;
  color: #5e5873;
}

.stat-value.text-warning {
  color: #ff9f43;
}

.stat-value.text-info {
  color: #00cfe8;
}

.stat-label {
  font-size: 0.857rem;
  color: #b9b9c3;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 500;
}

.operational-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.op-stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.75rem 1rem;
  background: #fff;
  border: 1px solid #ebe9f1;
  border-radius: 0.428rem;
  min-width: 80px;
}

.op-stat-value {
  font-size: 1.5rem;
  font-weight: 600;
  color: #5e5873;
}

.op-stat-label {
  font-size: 0.75rem;
  color: #b9b9c3;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.checkpoint-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.cp-stat {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.75rem;
  background: #f8f8f8;
  border-radius: 0.358rem;
  border: 1px solid #ebe9f1;
}

.cp-stat-name {
  font-size: 0.857rem;
  font-weight: 500;
  color: #5e5873;
}

.cp-stat-count {
  font-size: 0.857rem;
  font-weight: 600;
  color: #7367f0;
}
</style>

